import { FC } from 'react';
import {
  DashboardContainer, DashboardContainerBody, DashboardContainerHeader, JustifiedRow, ResolvedText, ScrollableList,
  SideDrawerLink, StyledTitle
} from '../MoaDashboard.styles';
import { ActivationsContainerProps } from './ActivationsContainer.types';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import Divider from '@mui/material/Divider';
import { ExtractJobStatus } from '../../../services/dashboard/dashboard.types';
import { ITooltipParams } from 'ag-grid-community';
import FailedAlert from "../FailedAlert";
import ExtractMetricsDisplay from "../ExtractMetricsDisplay";
import EventProgressBar from "../EventProgressBar";

const ActivationsContainer: FC<ActivationsContainerProps> = (
    {
      inProgress,
      upNext,
      done,
      setDrawerColumnDefs,
      setOpenDrawer,
      setDrawerType,
      setFetchExtractionsFailure,
      setExtractionAndActivationsJobId,
      setRequestUserName
    }
) => {
  const muiTheme = createTheme();

  const activationsColumnDefs = [
    {
      minWidth: 80,
      maxWidth: 100,
      field: 'label',
      headerName: 'Store#',
      sortable: true,
      floatingFilter: false
    },
    {
      minWidth: 80,
      maxWidth: 100,
      field: 'marketNo',
      headerName: 'FZ Code',
      sortable: false,
      floatingFilter: false
    },
    {
      minWidth: 400,
      field: 'reason',
      headerName: 'Error Reason',
      sortable: false,
      floatingFilter: false,
      tooltipValueGetter: (params: ITooltipParams) => params.value
    },
    {
      minWidth: 80,
      maxWidth: 100,
      field: 'isResolved',
      headerName: '',
      sortable: false,
      floatingFilter: false,
      cellRenderer: (params: any) => {
        if (params.value) {
          return <ResolvedText>Resolved</ResolvedText>;
        }
        else {
          return '';
        }
      }
    }
  ];

  const defineDrawer = (progressMetrics: ExtractJobStatus) => {
    setDrawerColumnDefs(activationsColumnDefs);
    setFetchExtractionsFailure(true);
    setOpenDrawer(true);
    setDrawerType('activate');
    setExtractionAndActivationsJobId(progressMetrics.jobId);
    setRequestUserName(progressMetrics.userName);
  };

  const renderStatus = (
      progressMetrics: ExtractJobStatus
  ) => {
    return (
        <>
          {progressMetrics.failed !== 0 &&
            <>
              {progressMetrics.failed > progressMetrics.resolved
                  ? <FailedAlert
                      count={progressMetrics.failed - progressMetrics.resolved}
                      openDrawer={() => defineDrawer(progressMetrics)}
                      id={progressMetrics.jobId}
                  />
                  : <SideDrawerLink
                      dataTestId={'resolvedText-' + progressMetrics.jobId}
                      label={progressMetrics.resolved + ' resolved'}
                      to={''}
                      onClick={() => defineDrawer(progressMetrics)}
                  />
              }
            </>
          }
        </>
    )
  };

  const renderInProgress = () => {
    return (
        <ScrollableList data-testid="activateInProgressContainer" style={{ height: '160px' }}>
          {inProgress.length === 0
              ? <StyledTitle>No activations in progress</StyledTitle>
              : inProgress.map((inProgressMetrics) =>
                  (
                      <JustifiedRow key={`activation-in-progress-${inProgressMetrics.jobId}`}>
                        <EventProgressBar
                            name={`${inProgressMetrics.extractType} for ${inProgressMetrics.userName}`}
                            processed={inProgressMetrics.processed}
                            total={inProgressMetrics.total}
                        />
                        {renderStatus(inProgressMetrics)}
                      </JustifiedRow>
                  )
              )
          }
        </ScrollableList>
    );
  };

  return (
      <DashboardContainer data-testid={'activationsContainer'} style={{ minHeight: '400px', maxHeight: '600px' }}>
        <DashboardContainerHeader data-testid="activationsContainerHeader">Activations</DashboardContainerHeader>
        <DashboardContainerBody>
          <ThemeProvider theme={muiTheme} key={'time-picker-mui-theme'}>
            {renderInProgress()}
            <Divider orientation="horizontal" variant={'fullWidth'} flexItem/>
            <ExtractMetricsDisplay
                extractMetrics={upNext}
                title={'Up next'}
                emptyText={'No activations waiting'}
                openDrawer={defineDrawer}
                testName={'upNextActivate'}
            />
            <Divider orientation="horizontal" variant={'fullWidth'} flexItem/>
            <ExtractMetricsDisplay
                extractMetrics={done}
                title={'Done'}
                emptyText={'No activations'}
                openDrawer={defineDrawer}
                testName={'doneActivate'}
            />
          </ThemeProvider>
        </DashboardContainerBody>
      </DashboardContainer>
  );
};

export default ActivationsContainer;
